import { createContext, useCallback, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { shouldClearPortalStateForAuthSessionChange } from '@/lib/portalAuthSession';
import {
  resolveCurrentPortalIdentity,
  type PortalIdentityResolverResult,
} from '@/lib/portalIdentityResolver';

type PortalIdentityResolverClient = Parameters<typeof resolveCurrentPortalIdentity>[0];

interface SeekAuthContextType {
  session: Session | null;
  user: User | null;
  profile: {
    full_name: string;
    email: string;
    org_id: string | null;
    user_type: string;
  } | null;
  loading: boolean;
  portalIdentityDiagnostic: PortalIdentityResolverResult | null;
  portalIdentityLoading: boolean;
  refreshProfile: () => Promise<void>;
  refreshPortalIdentityDiagnostic: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SeekAuthContext = createContext<SeekAuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  portalIdentityDiagnostic: null,
  portalIdentityLoading: false,
  refreshProfile: async () => {},
  refreshPortalIdentityDiagnostic: async () => {},
  signOut: async () => {},
});

export const useSeekAuth = () => useContext(SeekAuthContext);

export function SeekAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<SeekAuthContextType['profile']>(null);
  const [loading, setLoading] = useState(true);
  const [portalIdentityDiagnostic, setPortalIdentityDiagnostic] =
    useState<PortalIdentityResolverResult | null>(null);
  const [portalIdentityLoading, setPortalIdentityLoading] = useState(false);
  const portalIdentityRequestSeqRef = useRef(0);
  const portalIdentityInFlightUserRef = useRef<string | null>(null);
  const activeSeekUserIdRef = useRef<string | null>(null);

  const clearPortalIdentityDiagnostic = useCallback(() => {
    portalIdentityRequestSeqRef.current += 1;
    portalIdentityInFlightUserRef.current = null;
    setPortalIdentityDiagnostic(null);
    setPortalIdentityLoading(false);
  }, []);

  const runPortalIdentityDiagnostic = useCallback(
    async (userId: string, options: { force?: boolean } = {}) => {
      if (
        !options.force
        && portalIdentityInFlightUserRef.current === userId
      ) {
        return;
      }

      const requestSeq = portalIdentityRequestSeqRef.current + 1;
      portalIdentityRequestSeqRef.current = requestSeq;
      portalIdentityInFlightUserRef.current = userId;
      setPortalIdentityLoading(true);

      try {
        const result = await resolveCurrentPortalIdentity(
          seekSupabase as unknown as PortalIdentityResolverClient,
          'seek',
        );

        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic(result);
        }
      } catch {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic({
            ok: false,
            ready: false,
            reason: 'resolver_error',
            portal_auth_identity_id: null,
            portal: null,
            user_type: null,
            auth_provider: null,
            auth_instance_id: null,
            auth_user_id: null,
            status: null,
            auth_identity_kind: null,
          });
        }
      } finally {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          portalIdentityInFlightUserRef.current = null;
          setPortalIdentityLoading(false);
        }
      }
    },
    [],
  );

  const fetchProfile = useCallback(async (userId: string, authEmail?: string | null) => {
    const { data, error } = await seekSupabase
      .from('seek_profiles')
      .select('full_name, email')
      .eq('user_id', userId)
      .single();

    if (activeSeekUserIdRef.current !== userId) {
      return;
    }

    if (!error && data) {
      const nextProfile = { ...data, email: authEmail ?? data.email, org_id: null, user_type: 'candidate' };
      setProfile(nextProfile);
      void runPortalIdentityDiagnostic(userId);
    } else {
      clearPortalIdentityDiagnostic();
    }
  }, [clearPortalIdentityDiagnostic, runPortalIdentityDiagnostic]);

  const refreshPortalIdentityDiagnostic = useCallback(async () => {
    if (!user?.id || profile?.user_type !== 'candidate') {
      clearPortalIdentityDiagnostic();
      return;
    }

    await runPortalIdentityDiagnostic(user.id, { force: true });
  }, [clearPortalIdentityDiagnostic, profile?.user_type, runPortalIdentityDiagnostic, user?.id]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    await fetchProfile(user.id, user.email);
  }, [fetchProfile, user?.email, user?.id]);

  useEffect(() => {
    const { data: { subscription } } = seekSupabase.auth.onAuthStateChange(
      async (_event, session) => {
        const previousUserId = activeSeekUserIdRef.current;
        const nextUserId = session?.user?.id ?? null;
        const shouldClearState = shouldClearPortalStateForAuthSessionChange(previousUserId, nextUserId);
        activeSeekUserIdRef.current = nextUserId;
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          if (shouldClearState) {
            setProfile(null);
            clearPortalIdentityDiagnostic();
          }
          setLoading(true);
          setTimeout(() => {
            void fetchProfile(session.user.id, session.user.email).finally(() => {
              if (activeSeekUserIdRef.current === session.user.id) {
                setLoading(false);
              }
            });
          }, 0);
        } else {
          setProfile(null);
          clearPortalIdentityDiagnostic();
          setLoading(false);
        }
      }
    );

    seekSupabase.auth.getSession().then(({ data: { session } }) => {
      const previousUserId = activeSeekUserIdRef.current;
      const nextUserId = session?.user?.id ?? null;
      const shouldClearState = shouldClearPortalStateForAuthSessionChange(previousUserId, nextUserId);
      activeSeekUserIdRef.current = nextUserId;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        if (shouldClearState) {
          setProfile(null);
          clearPortalIdentityDiagnostic();
        }
        setLoading(true);
        void fetchProfile(session.user.id, session.user.email).finally(() => {
          if (activeSeekUserIdRef.current === session.user.id) {
            setLoading(false);
          }
        });
      } else {
        clearPortalIdentityDiagnostic();
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [clearPortalIdentityDiagnostic, fetchProfile]);

  const signOut = async () => {
    await seekSupabase.auth.signOut();
    activeSeekUserIdRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
    clearPortalIdentityDiagnostic();
  };

  return (
    <SeekAuthContext.Provider
      value={{
        session,
        user,
        profile,
        loading,
        portalIdentityDiagnostic,
        portalIdentityLoading,
        refreshProfile,
        refreshPortalIdentityDiagnostic,
        signOut,
      }}
    >
      {children}
    </SeekAuthContext.Provider>
  );
}
